import { MenuItem, isMeetingRoomExtras } from 'types/menuTypes';
import { logger } from 'utils/logger';

export interface MenuPath {
  items: MenuItem[];
  level: 'first' | 'second' | 'third';
  documentId: string;
  path: string;
}

/**
 * Finds the complete path of a menu item by URL path
 * Returns the full hierarchical path (parent -> child -> grandchild)
 */
export const findMenuPathByUrl = (menu: MenuItem[], targetPath: string): MenuPath | null => {
  const decodedPath = decodeURIComponent(targetPath);
  if (!menu || menu.length === 0) {
    logger.debug('[findMenuPathByUrl] Menu empty, waiting for loading');
    return null;
  }

  logger.debug(
    '[findMenuPathByUrl] Searching for path:',
    targetPath,
    'in menu with',
    menu.length,
    'items'
  );

  const findInMenu = (
    items: MenuItem[],
    path: string,
    parentPath: MenuItem[] = []
  ): MenuItem[] | null => {
    for (const item of items) {
      const currentPath = [...parentPath, item];

      if (item.path.trim() === decodedPath.trim()) {
        logger.debug('[findMenuPathByUrl] Menu found:', { data: item.label });
        return currentPath;
      }

      if (item.subMenu?.length) {
        const found = findInMenu(item.subMenu, path, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const menuPath = findInMenu(menu, decodedPath);
  if (!menuPath?.length) {
    logger.debug('[findMenuPathByUrl] Menu not found for path:', { data: targetPath });
    return null;
  }

  const lastItem = menuPath[menuPath.length - 1];
  const level = menuPath.length === 1 ? 'first' : menuPath.length === 2 ? 'second' : 'third';

  // Extract documentId
  let documentId = lastItem.feature?.documentId || '';
  if (!documentId && lastItem.extras && isMeetingRoomExtras(lastItem.extras)) {
    documentId = lastItem.extras.internal_id || '';
  }

  logger.debug('[findMenuPathByUrl] Menu found successfully:', {
    path: lastItem.path,
    label: lastItem.label,
    level,
    documentId,
  });

  return {
    items: menuPath,
    level,
    documentId,
    path: lastItem.path,
  };
};

/**
 * Finds the complete path of a menu item by documentId
 */
export const findMenuPathByDocumentId = (
  menu: MenuItem[],
  targetId: string,
  targetPath?: string
): MenuPath | null => {
  const findInMenu = (
    items: MenuItem[],
    id: string,
    path?: string,
    parentPath: MenuItem[] = []
  ): MenuItem[] | null => {
    for (const item of items) {
      const currentPath = [...parentPath, item];

      // If we have a specific targetPath, prioritize exact match
      if (path) {
        if (item.path.trim() === path?.trim()) {
          const hasMatchingDocumentId =
            item.feature?.documentId === id ||
            (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id === id);

          if (hasMatchingDocumentId) {
            return currentPath;
          }
        }
      } else {
        // If we don't have targetPath, use the original logic
        if (
          item.feature?.documentId === id ||
          (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id === id)
        ) {
          return currentPath;
        }
      }

      // Check submenus recursively
      if (item.subMenu?.length) {
        const found = findInMenu(item.subMenu, id, path, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // If we have a specific path, try to find by path first
  if (targetPath) {
    const menuPathByUrl = findMenuPathByUrl(menu, targetPath);
    if (menuPathByUrl) {
      const lastItem = menuPathByUrl.items[menuPathByUrl.items.length - 1];
      const hasMatchingDocumentId =
        lastItem.feature?.documentId === targetId ||
        (lastItem.extras &&
          isMeetingRoomExtras(lastItem.extras) &&
          lastItem.extras.internal_id === targetId);

      if (hasMatchingDocumentId) {
        logger.debug('[menuUtils] Menu found by path + documentId:', { data: targetPath });
        return menuPathByUrl;
      }
    }
  }

  const menuPath = findInMenu(menu, targetId, targetPath);
  if (!menuPath?.length) return null;

  const lastItem = menuPath[menuPath.length - 1];
  const level = menuPath.length === 1 ? 'first' : menuPath.length === 2 ? 'second' : 'third';

  // Extract documentId
  let documentId = lastItem.feature?.documentId || '';
  if (!documentId && lastItem.extras && isMeetingRoomExtras(lastItem.extras)) {
    documentId = lastItem.extras.internal_id || '';
  }

  return {
    items: menuPath,
    level,
    documentId,
    path: lastItem.path,
  };
};

/**
 * Finds the first valid menu item (with documentId or internal_id)
 */
export const findFirstValidMenu = (menu: MenuItem[]): MenuItem | null => {
  for (const item of menu) {
    // Check if it has documentId in the feature
    if (item.feature?.documentId) {
      return item;
    }
    // Check if it's a meeting room menu and has internal_id
    if (item.extras && isMeetingRoomExtras(item.extras) && item.extras.internal_id) {
      return item;
    }
    // Check submenus
    if (item.subMenu?.length) {
      const found = findFirstValidMenu(item.subMenu);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Finds a smart fallback menu based on the current context
 */
export const findSmartFallback = (
  menu: MenuItem[],
  currentPath: string,
  currentLevel: 'first' | 'second' | 'third' | null
): MenuPath | null => {
  logger.debug('[menuUtils] Searching for smart fallback for:', { data: currentPath });

  // 1. Try to find a menu similar to the current one
  const currentMenuPath = findMenuPathByUrl(menu, currentPath);
  if (currentMenuPath?.items.length) {
    const parentMenu = currentMenuPath.items[0];
    if (parentMenu.subMenu?.length) {
      const firstSubMenu = parentMenu.subMenu[0];
      const subMenuPath = findMenuPathByDocumentId(
        menu,
        firstSubMenu.feature?.documentId ||
          (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
            ? firstSubMenu.extras.internal_id
            : '') ||
          '',
        firstSubMenu.path
      );
      if (subMenuPath) {
        logger.debug('[menuUtils] Fallback 1: Submenu of current menu:', { data: subMenuPath.path });
        return subMenuPath;
      }
    }
  }

  // 2. Try to find an active menu at the same level
  if (currentLevel) {
    const currentLevelNumber = currentLevel === 'first' ? 1 : currentLevel === 'second' ? 2 : 3;
    const menusAtSameLevel = menu.filter(item => {
      if (currentLevelNumber === 1) return item.level === 'first' && item.isActive;
      if (currentLevelNumber === 2) return item.level === 'second' && item.isActive;
      return item.level === 'third' && item.isActive;
    });

    if (menusAtSameLevel.length > 0) {
      const firstMenu = menusAtSameLevel[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstMenu.feature?.documentId ||
          (firstMenu.extras && isMeetingRoomExtras(firstMenu.extras)
            ? firstMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        logger.debug('[menuUtils] Fallback 2: Menu at same level:', { data: menuPath.path });
        return menuPath;
      }
    }
  }

  // 3. Last resort: first active menu
  const firstActiveMenu = menu.find(item => item.isActive);
  if (firstActiveMenu?.subMenu?.length) {
    const firstSubMenu = firstActiveMenu.subMenu[0];
    const menuPath = findMenuPathByDocumentId(
      menu,
      firstSubMenu.feature?.documentId ||
        (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
          ? firstSubMenu.extras.internal_id
          : '') ||
        ''
    );
    if (menuPath) {
      logger.debug('[menuUtils] Fallback 3: First active menu:', { data: menuPath.path });
      return menuPath;
    }
  }

  logger.warn('[menuUtils] No fallback found');
  return null;
};

/**
 * Validates if a URL is valid based on the current menu
 */
export const isValidMenuUrl = (menu: MenuItem[], path: string): boolean => {
  return findMenuPathByUrl(menu, path) !== null;
};

/**
 * Validates if a route is valid based on the menu recursively
 * More robust than isValidMenuUrl for initialization validations
 */
export const isValidMenuRoute = (menu: MenuItem[], path: string): boolean => {
  const checkMenu = (menuItem: MenuItem): boolean => {
    if (menuItem.path.trim() === decodeURIComponent(path).trim()) return true;
    if (menuItem.subMenu) {
      return menuItem.subMenu.some(checkMenu);
    }
    return false;
  };

  return menu.some(checkMenu);
};

/**
 * Extracts documentId from a MenuItem
 */
export const extractDocumentId = (menuItem: MenuItem): string | null => {
  // First try to get documentId from feature
  let documentId = menuItem.feature?.documentId || null;

  // If not found, try to get internal_id for meeting room menus
  if (!documentId && menuItem.extras) {
    const extras = menuItem.extras;
    if (isMeetingRoomExtras(extras)) {
      documentId = extras.internal_id || null;
    }
  }

  return documentId;
};
